import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface GuiaFormatacaoProps {
  aberto: boolean;
  onFechar: () => void;
}

const GuiaFormatacao: React.FC<GuiaFormatacaoProps> = ({ aberto, onFechar }) => {
  const [copiado, setCopiado] = useState<string | null>(null);

  const copiarTexto = (texto: string, id: string) => {
    navigator.clipboard.writeText(texto);
    setCopiado(id);
    setTimeout(() => setCopiado(null), 2000);
  };

  const ExemploBox: React.FC<{ titulo: string; codigo: string; id: string }> = ({ titulo, codigo, id }) => (
    <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, position: 'relative' }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        {titulo}
      </Typography>
      <Box sx={{
        bgcolor: 'background.paper',
        p: 1.5,
        borderRadius: 1,
        fontFamily: 'monospace',
        fontSize: '0.9rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
      }}>
        {codigo}
      </Box>
      <IconButton
        size="small"
        onClick={() => copiarTexto(codigo, id)}
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
      {copiado === id && (
        <Chip 
          label="Copiado!" 
          color="success" 
          size="small" 
          sx={{ position: 'absolute', top: 8, right: 48 }}
        />
      )}
    </Box>
  );

  return (
    <Dialog 
      open={aberto} 
      onClose={onFechar}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutlineIcon color="primary" />
          <Typography variant="h6">Guia de Formatação de Avisos</Typography>
        </Box>
        <IconButton onClick={onFechar}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Dica:</strong> Clique no ícone de copiar para usar os exemplos diretamente!
        </Alert>

        {/* TÍTULOS */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">📌 Títulos</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Use <code>#</code> antes do texto. Quanto mais <code>#</code>, menor o título.
            </Typography>
            
            <ExemploBox
              titulo="Título Grande"
              codigo="# Título Grande"
              id="titulo1"
            />
            
            <ExemploBox
              titulo="Título Médio"
              codigo="## Título Médio"
              id="titulo2"
            />
            
            <ExemploBox
              titulo="Título Pequeno"
              codigo="### Título Pequeno"
              id="titulo3"
            />
          </AccordionDetails>
        </Accordion>

        {/* NEGRITO E ITÁLICO */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">✨ Negrito e Itálico</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ExemploBox
              titulo="Negrito"
              codigo="**Este texto está em negrito**"
              id="negrito"
            />
            
            <ExemploBox
              titulo="Itálico"
              codigo="*Este texto está em itálico*"
              id="italico"
            />
            
            <ExemploBox
              titulo="Negrito + Itálico"
              codigo="***Este texto está em negrito e itálico***"
              id="negrito-italico"
            />
          </AccordionDetails>
        </Accordion>

        {/* LINKS */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">🔗 Links</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Use <code>[Texto que aparece](endereço do link)</code>
            </Typography>
            
            <ExemploBox
              titulo="Link para site"
              codigo="Acesse o [Google](https://google.com) para buscar."
              id="link1"
            />
            
            <ExemploBox
              titulo="Link para email"
              codigo="Entre em contato: [suporte@bravo.bet](mailto:suporte@bravo.bet)"
              id="link2"
            />
            
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Importante:</strong> Os links abrem em uma nova aba automaticamente!
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* IMAGENS */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">🖼️ Imagens</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Passo a passo:</strong>
              <ol style={{ marginTop: 8, marginBottom: 0 }}>
                <li>Hospede a imagem no Imgur (https://imgur.com)</li>
                <li>Clique com botão direito na imagem</li>
                <li>Escolha "Copiar endereço da imagem"</li>
                <li>Cole o link no formato abaixo</li>
              </ol>
            </Alert>
            
            <Typography variant="body2" sx={{ mb: 2 }}>
              Use <code>![Descrição da imagem](link da imagem)</code>
            </Typography>
            
            <ExemploBox
              titulo="Exemplo com imagem real"
              codigo="![Minha imagem](https://i.pinimg.com/736x/16/54/90/1654904fe1b7df921ce74020484ac04e.jpg)"
              id="imagem1"
            />
            
            <Alert severity="success" sx={{ mt: 2 }}>
              <strong>Dica:</strong> Cole o link da imagem no navegador para testar se está correto!
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* LISTAS */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">📋 Listas</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ExemploBox
              titulo="Lista com marcadores"
              codigo={`- Item 1\n- Item 2\n- Item 3`}
              id="lista1"
            />
            
            <ExemploBox
              titulo="Lista numerada"
              codigo={`1. Primeiro item\n2. Segundo item\n3. Terceiro item`}
              id="lista2"
            />
            
            <ExemploBox
              titulo="Lista com sub-itens"
              codigo={`- Item principal\n  - Sub-item 1\n  - Sub-item 2\n- Outro item`}
              id="lista3"
            />
          </AccordionDetails>
        </Accordion>

        {/* CITAÇÕES */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">💬 Citações</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Use <code>&gt;</code> antes do texto
            </Typography>
            
            <ExemploBox
              titulo="Citação simples"
              codigo="> Esta é uma citação importante"
              id="citacao1"
            />
            
            <ExemploBox
              titulo="Citação com múltiplas linhas"
              codigo={`> Linha 1 da citação\n> Linha 2 da citação\n> Linha 3 da citação`}
              id="citacao2"
            />
          </AccordionDetails>
        </Accordion>

        {/* EXEMPLO COMPLETO */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">🎨 Exemplo Completo</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ExemploBox
              titulo="Aviso completo com tudo"
              codigo={`# Reunião Importante

Olá equipe!

Teremos uma reunião **amanhã às 14h** na sala de conferências.

## Tópicos:
- Revisão de metas
- Novos projetos
- Feedback geral

## Link útil:
Acesse a [pauta da reunião](https://exemplo.com)

## Imagem:
![Logo](https://i.pinimg.com/736x/16/54/90/1654904fe1b7df921ce74020484ac04e.jpg)

> "A participação de todos é fundamental!"

**Não faltem!**`}
              id="completo"
            />
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 3 }} />

        {/* DICAS FINAIS */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>💡 Dicas Importantes</Typography>
          
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>✅ FAÇA:</strong>
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Use títulos para organizar o conteúdo</li>
              <li>Use negrito para destacar informações importantes</li>
              <li>Teste os links antes de publicar</li>
              <li>Use imagens para ilustrar melhor</li>
            </ul>
          </Alert>
          
          <Alert severity="error">
            <strong>❌ NÃO FAÇA:</strong>
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Não abuse do negrito (perde o efeito)</li>
              <li>Não esqueça o <code>!</code> antes de <code>[</code> nas imagens</li>
              <li>Não use espaços entre <code>]</code> e <code>(</code> nos links</li>
              <li>Não use imagens muito grandes (deixa lento)</li>
            </ul>
          </Alert>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GuiaFormatacao;
